import { Helmet } from 'react-helmet-async';

const BASE = 'https://proposai.ca';
const EN_PREFIX_RE = new RegExp(`^${BASE}/en(/|$)`);

/**
 * SEO Head Component
 *
 * Provides meta tags, Open Graph, Twitter Cards, and structured data for SEO.
 *
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description
 * @param {string} props.image - Social share image URL
 * @param {string} props.url - Canonical URL
 * @param {string} props.type - Open Graph type (default: "website")
 * @param {Object} props.structuredData - JSON-LD structured data (optional)
 */
export default function SEOHead({
  title,
  description,
  image = 'https://proposai.ca/og-image.png',
  url,
  type = 'website',
  structuredData = null,
  noIndex = false,
  lang = 'en',
}) {
  const fullTitle = title ? `${title} | ProposAI` : 'ProposAI — AI-Powered RFP Platform';
  const defaultDescription = 'Create professional RFPs with AI, connect with qualified vendors. Bilingual EN/FR, PIPEDA-compliant, built in Canada.';
  const metaDescription = description || defaultDescription;

  const frUrl = url
    ? url.replace(EN_PREFIX_RE, `${BASE}$1`).replace(/\/$/, '') || BASE
    : null;

  const enUrl = frUrl
    ? frUrl === BASE ? `${BASE}/en` : `${BASE}/en${frUrl.replace(BASE, '')}`
    : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={lang === 'fr' ? 'fr' : 'en'} />
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      {noIndex && <meta name="robots" content="noindex, follow" />}
      {url && <link rel="canonical" href={url} />}

      {/* Hreflang for bilingual content */}
      {frUrl && enUrl && (
        <>
          <link rel="alternate" hreflang="fr-ca" href={frUrl} />
          <link rel="alternate" hreflang="en-ca" href={enUrl} />
          <link rel="alternate" hreflang="x-default" href={frUrl} />
        </>
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === 'fr' ? 'fr_CA' : 'en_CA'} />
      <meta property="og:locale:alternate" content={lang === 'fr' ? 'en_CA' : 'fr_CA'} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={metaDescription} />
      <meta property="twitter:image" content={image} />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
